import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import { apiRequest, apiFormRequest, photoSrc, shortDate, canSharePhotos, downloadPhoto, sharePhoto }
  from '../../utils';
import './EventSocial.css';

/* What an event leaves behind that is not a number: the pictures, what people said, and the
   host's word afterwards.

   One component, two places. The participant opens it under the event page; the host opens the
   same thing on the run screen. Nothing here decides who may do what — ``can_moderate`` comes
   from the server with the payload and every write is re-checked there, so the flag only decides
   what is worth drawing.

   ★ It renders NOTHING until the server says yes. The feature is Bravos-only and only for people
   who were at the event, and both answers are a status code — so a 400/403 simply leaves the
   section out, and a Hercules Trophy event page is unchanged without knowing this exists.

   ★ A photo is identified by its blob PATH: it has no row anywhere. Likes, tags and comments all
   travel with that string, which is why deleting one purges them server-side.

   ★ Rewriting is the one action ``can_moderate`` does NOT open: the host takes something down, it
   never edits it into words the author did not choose. So the pencil is drawn on your own only —
   as the server answers anyway. A REPLY carries the same two: it was somebody's sentence too, and
   it is a Post with a parent, so it is the same route. */

const MAX_BODY = 4000;

const Avatar = ({ url, name }) => (
  url
    ? <img className="es-avatar" src={url} alt="" />
    : <span className="es-avatar es-avatar--empty">{(name || '?').trim().charAt(0) || '?'}</span>
);

/* One like button, everywhere it appears: the count is part of it, because a heart with no number
   makes people press it to find out. */
const LikeButton = ({ liked, count, busy, onClick, label }) => (
  <button
    type="button"
    className={`es-act ${liked ? 'is-on' : ''}`}
    disabled={busy}
    onClick={onClick}
    aria-label={label}
  >
    <Icon name="heart" /> {count > 0 ? count : ''}
  </button>
);

/* Rewriting something, in the box it was written in. One component because a wall post and a
   reply are the same row seen at two sizes — and the same route answers both. */
const EditBox = ({ value, busy, t, onChange, onSave, onCancel }) => (
  <span className="es-compose">
    <textarea
      rows={3}
      maxLength={MAX_BODY}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
    <span className="es-compose__row">
      <button type="button" className="es-btn" disabled={busy || !value.trim()} onClick={onSave}>
        {t('save')}
      </button>
      <button type="button" className="es-btn es-btn--ghost" onClick={onCancel}>
        {t('cancel')}
      </button>
    </span>
  </span>
);

/* What you may do to words that are already there.

   ★ The pencil is the AUTHOR's alone; the bin is the author's OR the host's. A moderator takes
   something down, it never edits it into words its author did not choose. */
const OwnerActs = ({ post, me, canModerate, busy, t, onEdit, onDelete }) => (
  <>
    {post.author_id === me && (
      <button type="button" className="es-act" disabled={busy} onClick={onEdit}>
        <Icon name="pencil" /> {t('edit')}
      </button>
    )}
    {(canModerate || post.author_id === me) && (
      <button
        type="button"
        className="es-act"
        disabled={busy}
        onClick={() => {
          // eslint-disable-next-line no-alert
          if (!window.confirm(t('confirmDeletePost'))) return;
          onDelete();
        }}
      >
        <Icon name="trash" /> {t('delete')}
      </button>
    )}
  </>
);

/* Why a report has a picklist and not just a box. The queue in Machina triages on the reason —
   "sexual content" and "spam" are not read by the same person in the same hurry — and asking
   somebody who is upset to phrase the category themselves gets you a hundred spellings of one
   thing. The box is where they say what actually happened. */
const REPORT_REASONS = ['abuse', 'harassment', 'sexual', 'spam', 'privacy', 'other'];

/* Saying "this should not be here".

   ★ It says, in the first line, that nothing is being removed. That sentence is the whole
   difference between this and the delete next to it: content that vanished the moment one person
   objected would hand a veto to whoever objects first, and somebody who expects the post to
   disappear and watches it stay will simply report it again, angrier. */
const ReportBox = ({ busy, t, onCancel, onSend }) => {
  const [reason, setReason] = useState('abuse');
  const [note, setNote] = useState('');
  return (
    <span className="es-compose es-report">
      <span className="es-meta">{t('reportExplains')}</span>
      <select
        className="es-report__reason"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      >
        {REPORT_REASONS.map((r) => (
          <option key={r} value={r}>{t(`reportReason_${r}`)}</option>
        ))}
      </select>
      <textarea
        rows={2}
        maxLength={500}
        value={note}
        placeholder={t('reportNotePlaceholder')}
        onChange={(e) => setNote(e.target.value)}
      />
      <span className="es-compose__row">
        <button type="button" className="es-btn" disabled={busy}
                onClick={() => onSend(reason, note.trim())}>
          {t('reportSend')}
        </button>
        <button type="button" className="es-btn es-btn--ghost" onClick={onCancel}>
          {t('cancel')}
        </button>
      </span>
    </span>
  );
};

/* The flag, wherever somebody else's words are. Never on your own: you have a bin for those. */
const ReportAct = ({ id, ctx }) => (
  <button
    type="button"
    className="es-act"
    disabled={ctx.busy}
    onClick={() => ctx.setReporting(ctx.reporting === id ? null : id)}
  >
    <Icon name="flag" /> {ctx.t('report')}
  </button>
);

/* One line of somebody else's conversation, drawn in the three places it appears: under a wall
   post, under a picture in the lightbox, and under the same picture's card on the wall.

   ``path`` is the only thing that differs — the wall's route or the photo's — which is what makes
   this one component rather than three copies. ``ctx`` is the screen's state handed down whole,
   because every one of these rows is driven by the same single ``editing`` id. */
const CommentRow = ({ c, path, ctx }) => (
  <span className="es-post es-post--comment">
    <Avatar url={c.author_photo} name={c.author} />
    <span className="es-post__body">
      <span className="es-post__who">{c.author}</span>
      {ctx.editing === c.id ? (
        <EditBox
          value={ctx.editDraft} busy={ctx.busy} t={ctx.t}
          onChange={ctx.setEditDraft}
          onSave={() => ctx.saveEdit(path)}
          onCancel={() => ctx.setEditing(null)}
        />
      ) : (
        <span className="es-post__text">{c.body}</span>
      )}
      {ctx.editing !== c.id && (
        <span className="es-acts">
          <OwnerActs
            post={c} me={ctx.me} canModerate={ctx.canModerate} busy={ctx.busy} t={ctx.t}
            onEdit={() => ctx.startEdit(c)}
            onDelete={() => ctx.removePost(path)}
          />
          {c.author_id !== ctx.me && <ReportAct id={c.id} ctx={ctx} />}
        </span>
      )}
      {ctx.reporting === c.id && (
        <ReportBox
          busy={ctx.busy} t={ctx.t}
          onCancel={() => ctx.setReporting(null)}
          onSend={ctx.sendReport(ctx.reportPath(c.id))}
        />
      )}
    </span>
  </span>
);

/* ``signedIn`` defaults to true so the run screen, which nobody reaches signed out, passes
   nothing. On a PUBLIC event page the caller knows the answer from its own payload and says so:
   there is nothing here for a stranger, and asking anyway costs a request whose refusal used to
   be read as an expired session. See the note in utils.apiRequest. */
const EventSocial = ({ eventId, className, signedIn = true }) => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);
  // Kept apart from `busy`: fetching a picture to save is not a reason to freeze liking or tagging.
  const [saving, setSaving] = useState(false);

  // A property of the browser, not of the photograph, so it is asked once.
  const [shareable] = useState(canSharePhotos);
  const [notice, setNotice] = useState(null);
  const [open, setOpen] = useState(null);          // blob name of the photo in the lightbox
  const [wallDraft, setWallDraft] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [replyDraft, setReplyDraft] = useState('');
  const [editing, setEditing] = useState(null);    // id of the post being rewritten
  const [editDraft, setEditDraft] = useState('');
  const [photoComment, setPhotoComment] = useState('');
  const [reporting, setReporting] = useState(null); // id of the thing being objected to
  const [recapDraft, setRecapDraft] = useState(null);
  const [people, setPeople] = useState(null);      // tag targets, fetched on first use
  const [tagging, setTagging] = useState(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const load = useCallback(() => {
    if (!signedIn) { setHidden(true); return; }
    apiRequest('GET', `/event/${eventId}/social`)
      .then(setData)
      .catch(() => setHidden(true));
  }, [eventId, signedIn]);

  useEffect(() => { load(); }, [load]);

  /* Every write answers with the piece it changed, so the screen never re-reads the whole page
     to show one new like. */
  /* Save the picture, or hand it to the phone's share sheet. Fed ``photoSrc`` rather than the bare
     blob address, so a picture that has been turned is saved the way it is being looked at. The
     cross-origin rule that makes this necessary is explained once, in utils. */
  const savePhoto = async (pic, action) => {
    if (saving || !pic) return;
    setSaving(true);
    try {
      await action(photoSrc(pic), pic.name);
    } finally {
      setSaving(false);
    }
  };

  const act = (promise) => {
    if (busy) return Promise.resolve();
    setBusy(true);
    setNotice(null);
    return promise
      .then((res) => setData((d) => ({ ...d, ...(res || {}) })))
      .catch((err) => setNotice(t(err.message, { defaultValue: t('saveFailed') })))
      .finally(() => setBusy(false));
  };

  /* Two routes, one shape. A wall post and a reply under it are the same call (a reply is just a
     Post with a parent); a comment under a picture is the photo route, because its subject is a
     blob path rather than the event. The caller hands in which. */
  const wallPath = (id) => `/event/${eventId}/wall/${id}`;
  const photoPath = (id) => `/event/${eventId}/photos/comment/${id}`;
  const startEdit = (post) => { setEditing(post.id); setEditDraft(post.body || ''); };
  const saveEdit = (path) => {
    const body = editDraft.trim();
    setEditing(null);
    act(apiRequest('PUT', path, { body }));
  };
  const removePost = (path) => act(apiRequest('DELETE', path));

  /* Filing an objection. Deliberately NOT routed through ``act``: that helper's job is "a write
     answered with the piece it changed", and here the answer is a sentence rather than a piece —
     nothing on the screen moves, which is exactly what the person has to be told. */
  const reportPath = (id) => `/event/${eventId}/wall/${id}/report`;
  const sendReport = (path, extra) => (reason, note) => {
    setReporting(null);
    if (busy) return;
    setBusy(true);
    setNotice(null);
    apiRequest('POST', path, { ...(extra || {}), reason, note })
      .then((res) => { setData((d) => ({ ...d, ...(res || {}) })); setNotice(t('reportThanks')); })
      .catch((err) => setNotice(t(err.message, { defaultValue: t('saveFailed') })))
      .finally(() => setBusy(false));
  };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';                            // let the same file be picked again later
    if (!files.length) return;
    setBusy(true);
    setNotice(null);
    let failed = 0;
    let last = null;
    // One request per file: each needs its own slot in the album's counter.
    for (const file of files) {
      const form = new FormData();
      form.append('file', file);
      try {
        // eslint-disable-next-line no-await-in-loop
        last = await apiFormRequest(`/event/${eventId}/photos`, form);
      } catch {
        failed += 1;
      }
    }
    if (last) setData((d) => ({ ...d, ...last }));
    if (failed) setNotice(t('uploadFailed'));
    setBusy(false);
  };

  if (hidden || data === null) return null;

  const album = data.album || {};
  const photos = album.photos || [];
  const wall = data.wall || [];
  const recap = data.recap;
  const canModerate = !!data.can_moderate;
  // Absent means yes: an older payload (or a route that has not learned to say) must not silence
  // a whole event's wall.
  const canPost = data.can_post !== false;
  const photo = open ? photos.find((p) => p.name === open) : null;

  /* Everything a comment row needs, in one object — there is a single `editing` id on this
     screen, so every row anywhere is driven by the same state. */
  const ctx = { me: data.me, canModerate, busy, t, editing, editDraft,
                setEditDraft, setEditing, startEdit, saveEdit, removePost,
                reporting, setReporting, sendReport, reportPath };

  /* A wall card knows its picture by the path the DATABASE gave it, which is lower-cased; the
     album knows it by the blob's own spelling. This is the bridge, and it is also the test for
     whether the lightbox can open it at all — the album is paged, and a card may point at a
     picture that is not on the page in front of us. */
  const albumName = (name) => (photos.find((ph) => ph.name.toLowerCase() === name) || {}).name;

  const loadPeople = () => {
    setTagging(true);
    if (people === null) {
      apiRequest('GET', `/event/${eventId}/social/people`)
        .then((res) => setPeople(res.people || []))
        .catch(() => setPeople([]));
    }
  };

  return (
    <div className={`es ${className || ''}`}>
      {notice && <p className="es-notice">{notice}</p>}

      {/* --- the recap ------------------------------------------------------------------- */}
      {(recap || canModerate) && (
        <section className="es-block">
          <h2 className="es-title"><Icon name="megaphone" theme="primary" /> {t('eventRecap')}</h2>

          {recapDraft === null && recap && (
            <div className="es-recap">
              <p className="es-recap__body">{recap.body}</p>
              <span className="es-meta">
                {recap.author} · {shortDate(recap.created_at, i18n.language)}
              </span>
              {canModerate && (
                <div className="es-acts">
                  <button type="button" className="es-act" onClick={() => setRecapDraft(recap.body)}>
                    {t('edit')}
                  </button>
                  <button
                    type="button"
                    className="es-act"
                    disabled={busy}
                    onClick={() => {
                      // eslint-disable-next-line no-alert
                      if (!window.confirm(t('removeRecapConfirm'))) return;
                      act(apiRequest('DELETE', `/event/${eventId}/recap`));
                    }}
                  >
                    {t('remove')}
                  </button>
                </div>
              )}
            </div>
          )}

          {recapDraft === null && !recap && canModerate && (
            <button type="button" className="es-btn es-btn--ghost" onClick={() => setRecapDraft('')}>
              <Icon name="plus" /> {t('writeRecap')}
            </button>
          )}

          {recapDraft !== null && (
            <div className="es-compose">
              {/* A plain textarea, not a rich editor: the feed and the digest render this as
                  text, so anything typed as markup would arrive as markup. */}
              <textarea
                rows={5}
                maxLength={MAX_BODY}
                value={recapDraft}
                placeholder={t('recapPlaceholder')}
                onChange={(e) => setRecapDraft(e.target.value)}
              />
              <div className="es-acts">
                <button
                  type="button"
                  className="es-btn"
                  disabled={busy || !recapDraft.trim()}
                  onClick={() => { const body = recapDraft.trim(); setRecapDraft(null);
                    act(apiRequest('POST', `/event/${eventId}/recap`, { body })); }}
                >
                  {recap ? t('save') : t('publishRecap')}
                </button>
                <button type="button" className="es-btn es-btn--ghost"
                        onClick={() => setRecapDraft(null)}>
                  {t('cancel')}
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      {/* --- photos --------------------------------------------------------------------- */}
      <section className="es-block">
        <h2 className="es-title">
          <Icon name="camera" theme="primary" /> {t('eventPhotos')}
          {album.total > 0 && <span className="es-count">{album.total}</span>}
        </h2>

        {photos.length === 0 && <p className="es-empty">{t('noEventPhotos')}</p>}

        <div className="es-grid">
          {photos.map((p) => (
            <button key={p.name} type="button" className="es-thumb" onClick={() => setOpen(p.name)}>
              <img src={photoSrc(p)} alt="" loading="lazy" />
              {p.best && <span className="es-badge"><Icon name="star" theme="light" /></span>}
              <span className="es-thumb__foot">
                {p.likes > 0 && <><Icon name="heart" theme="light" /> {p.likes}</>}
                {p.comments.length > 0 && (
                  <><Icon name="comment" theme="light" /> {p.comments.length}</>
                )}
              </span>
            </button>
          ))}
        </div>

        {album.full ? (
          <p className="es-empty">{t('albumFull')}</p>
        ) : (
          <div className="es-acts">
            <button type="button" className="es-btn es-btn--ghost" disabled={busy}
                    onClick={() => cameraRef.current.click()}>
              <Icon name="camera" /> {t('openCamera')}
            </button>
            <button type="button" className="es-btn es-btn--ghost" disabled={busy}
                    onClick={() => galleryRef.current.click()}>
              <Icon name="photo-video" /> {busy ? t('loading') : t('uploadFromGallery')}
            </button>
          </div>
        )}
        <input type="file" accept="image/*" capture="environment" ref={cameraRef}
               style={{ display: 'none' }} onChange={upload} />
        <input type="file" accept="image/*" multiple ref={galleryRef}
               style={{ display: 'none' }} onChange={upload} />

        {album.total > photos.length && (
          <p className="es-empty">{t('showingNPhotos', { n: photos.length, total: album.total })}</p>
        )}
      </section>

      {/* --- one photo, full size ------------------------------------------------------- */}
      {photo && (
        <div className="es-lightbox" role="dialog">
          <button type="button" className="es-lightbox__close"
                  onClick={() => { setOpen(null); setTagging(false); setPhotoComment(''); }}>
            <Icon name="cross" theme="light" />
          </button>
          <div className="es-lightbox__inner">
            <img src={photoSrc(photo)} alt="" />

            <div className="es-acts">
              <LikeButton
                liked={photo.liked}
                count={photo.likes}
                busy={busy}
                label={t('like')}
                onClick={() => act(apiRequest('POST', `/event/${eventId}/photos/like`,
                                              { name: photo.name }))}
              />
              <button type="button" className="es-act" onClick={loadPeople}>
                <Icon name="user-add" /> {t('tagSomeone')}
              </button>
              {/* Share leads where the browser has a sheet for it — on a phone that is what people
                  mean by sharing a photo, and it skips the downloads folder entirely. */}
              {shareable && (
                <button type="button" className="es-act" disabled={saving}
                        onClick={() => savePhoto(photo, sharePhoto)}>
                  <Icon name="share" /> {t('share')}
                </button>
              )}
              <button type="button" className="es-act" disabled={saving}
                      onClick={() => savePhoto(photo, downloadPhoto)}>
                <Icon name="download" /> {t('download')}
              </button>
              {canModerate && (
                <button
                  type="button"
                  className={`es-act ${photo.best ? 'is-on' : ''}`}
                  disabled={busy}
                  onClick={() => act(apiRequest('POST', `/event/${eventId}/photos/best`,
                                                { name: photo.name }))}
                >
                  <Icon name="star" /> {t('markBest')}
                </button>
              )}
              {/* Straightening a picture is not moderating it — the photograph everybody uploaded
                  is the photograph everybody keeps, the right way up — so it is open to whoever
                  may take it down: the person who took it, and whoever runs the event. Two
                  buttons rather than one that cycles: a JPEG is re-encoded on every press. */}
              {photo.can_delete && (
                <>
                  <button
                    type="button"
                    className="es-act"
                    disabled={busy}
                    onClick={() => act(apiRequest('POST', `/event/${eventId}/photos/rotate`,
                                                  { name: photo.name, turns: -1 }))}
                  >
                    <Icon name="rotate-left" /> {t('rotateLeft')}
                  </button>
                  <button
                    type="button"
                    className="es-act"
                    disabled={busy}
                    onClick={() => act(apiRequest('POST', `/event/${eventId}/photos/rotate`,
                                                  { name: photo.name, turns: 1 }))}
                  >
                    <Icon name="rotate-right" /> {t('rotateRight')}
                  </button>
                </>
              )}
              {photo.can_delete && (
                <button
                  type="button"
                  className="es-act"
                  disabled={busy}
                  onClick={() => {
                    // eslint-disable-next-line no-alert
                    if (!window.confirm(t('confirmDeletePhoto'))) return;
                    setOpen(null);
                    act(apiRequest('DELETE', `/event/${eventId}/photos`, { name: photo.name }));
                  }}
                >
                  <Icon name="trash" /> {t('delete')}
                </button>
              )}
              {/* Asking for a photo to come down takes it down, now. Nobody waits for us to
                  agree that they may be out of a picture. */}
              {!photo.can_delete && (
                <button
                  type="button"
                  className="es-act"
                  disabled={busy}
                  onClick={() => {
                    // eslint-disable-next-line no-alert
                    const reason = window.prompt(t('reportPhotoReason'));
                    if (reason === null) return;
                    setOpen(null);
                    act(apiRequest('POST', `/event/${eventId}/photos/takedown`,
                                   { name: photo.name, reason }));
                  }}
                >
                  <Icon name="flag" /> {t('reportPhoto')}
                </button>
              )}
              {/* And the other kind of objection, which is not about being IN the picture: this
                  one removes nothing and goes to whoever runs the business unit. Two buttons
                  because they are two different asks — see routes/event_social. */}
              {!photo.can_delete && <ReportAct id={photo.name} ctx={ctx} />}
            </div>

            {reporting === photo.name && (
              <ReportBox
                busy={busy} t={t}
                onCancel={() => setReporting(null)}
                onSend={sendReport(`/event/${eventId}/photos/report`, { name: photo.name })}
              />
            )}

            {photo.tags.length > 0 && (
              <div className="es-tags">
                {photo.tags.map((tag) => (
                  <span key={tag.partner_id} className="es-tag">
                    {tag.name}
                    <button
                      type="button"
                      disabled={busy}
                      aria-label={t('remove')}
                      onClick={() => act(apiRequest('DELETE', `/event/${eventId}/photos/tag`,
                                                    { name: photo.name,
                                                      partner_id: tag.partner_id }))}
                    >
                      <Icon name="cross-small" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {tagging && (
              <div className="es-compose">
                <select
                  defaultValue=""
                  disabled={busy || people === null}
                  onChange={(e) => { const id = e.target.value; if (!id) return;
                    setTagging(false);
                    act(apiRequest('POST', `/event/${eventId}/photos/tag`,
                                   { name: photo.name, partner_id: id })); }}
                >
                  <option value="">{people === null ? t('loading') : t('tagSomeone')}</option>
                  {(people || [])
                    .filter((p) => !photo.tags.some((tag) => tag.partner_id === p.id))
                    .map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            )}

            {photo.comments.map((c) => (
              <CommentRow key={c.id} c={c} path={photoPath(c.id)} ctx={ctx} />
            ))}

            <div className="es-compose">
              <textarea
                rows={2}
                maxLength={MAX_BODY}
                value={photoComment}
                placeholder={t('commentPlaceholder')}
                onChange={(e) => setPhotoComment(e.target.value)}
              />
              <button
                type="button"
                className="es-btn"
                disabled={busy || !photoComment.trim()}
                onClick={() => { const body = photoComment.trim(); setPhotoComment('');
                  act(apiRequest('POST', `/event/${eventId}/photos/comment`,
                                 { name: photo.name, body })); }}
              >
                {t('post')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- the wall ------------------------------------------------------------------- */}
      <section className="es-block">
        <h2 className="es-title">
          <Icon name="comment" theme="primary" /> {t('eventWall')}
          {wall.length > 0 && <span className="es-count">{wall.length}</span>}
        </h2>

        {/* ★ The server refuses a restricted person's write either way (mutations/social is the
            choke point). This is so they read WHY here, instead of typing a paragraph and being
            told after they press Post. */}
        {canPost ? (
          <div className="es-compose">
            <textarea
              rows={3}
              maxLength={MAX_BODY}
              value={wallDraft}
              placeholder={t('wallPlaceholder')}
              onChange={(e) => setWallDraft(e.target.value)}
            />
            <button
              type="button"
              className="es-btn"
              disabled={busy || !wallDraft.trim()}
              onClick={() => { const body = wallDraft.trim(); setWallDraft('');
                act(apiRequest('POST', `/event/${eventId}/wall`, { body })); }}
            >
              {t('post')}
            </button>
          </div>
        ) : (
          <p className="es-empty">
            {t('postingRestricted')}
            {data.post_block_reason ? ` ${data.post_block_reason}` : ''}
          </p>
        )}

        {wall.length === 0 && <p className="es-empty">{t('noWallPosts')}</p>}

        {wall.map((p) => (p.kind === 'photo' ? (
          /* A picture people said something under. The card is a POINTER: these are the same
             comments the lightbox shows, so answering here answers there. Only pictures with
             something said under them get one — the album above already draws them all. */
          <div key={p.id} className="es-post">
            <Avatar url={p.author_photo} name={p.author} />
            <span className="es-post__body">
              <span className="es-post__who">
                {p.author}
                <span className="es-meta">{shortDate(p.created_at, i18n.language)}</span>
              </span>

              {albumName(p.photo.name) ? (
                <button
                  type="button"
                  className="es-wallphoto"
                  onClick={() => setOpen(albumName(p.photo.name))}
                >
                  <img src={p.photo.url} alt="" />
                </button>
              ) : (
                <span className="es-wallphoto"><img src={p.photo.url} alt="" /></span>
              )}

              {p.comments.map((c) => (
                <CommentRow key={c.id} c={c} path={photoPath(c.id)} ctx={ctx} />
              ))}

              <span className="es-acts">
                <button type="button" className="es-act"
                        onClick={() => { setReplyTo(replyTo === p.id ? null : p.id);
                          setReplyDraft(''); }}>
                  <Icon name="comment" /> {t('reply')}
                </button>
              </span>

              {replyTo === p.id && (
                <span className="es-compose">
                  <textarea
                    rows={2}
                    maxLength={MAX_BODY}
                    value={replyDraft}
                    placeholder={t('commentPlaceholder')}
                    onChange={(e) => setReplyDraft(e.target.value)}
                  />
                  <button
                    type="button"
                    className="es-btn"
                    disabled={busy || !replyDraft.trim()}
                    onClick={() => { const body = replyDraft.trim();
                      setReplyDraft(''); setReplyTo(null);
                      act(apiRequest('POST', `/event/${eventId}/photos/comment`,
                                     { name: p.photo.name, body })); }}
                  >
                    {t('post')}
                  </button>
                </span>
              )}
            </span>
          </div>
        ) : (
          <div key={p.id} className="es-post">
            <Avatar url={p.author_photo} name={p.author} />
            <span className="es-post__body">
              <span className="es-post__who">
                {p.author}
                <span className="es-meta">{shortDate(p.created_at, i18n.language)}</span>
              </span>
              {editing === p.id ? (
                <EditBox
                  value={editDraft} busy={busy} t={t}
                  onChange={setEditDraft}
                  onSave={() => saveEdit(wallPath(p.id))}
                  onCancel={() => setEditing(null)}
                />
              ) : (
                <span className="es-post__text">{p.body}</span>
              )}

              <span className="es-acts">
                <LikeButton
                  liked={p.liked}
                  count={p.likes}
                  busy={busy}
                  label={t('like')}
                  onClick={() => act(apiRequest('POST', `/event/${eventId}/wall/${p.id}/like`))}
                />
                <button type="button" className="es-act"
                        onClick={() => { setReplyTo(replyTo === p.id ? null : p.id);
                          setReplyDraft(''); }}>
                  <Icon name="comment" /> {t('reply')}
                </button>
                {editing !== p.id && (
                  <OwnerActs
                    post={p} me={data.me} canModerate={canModerate} busy={busy} t={t}
                    onEdit={() => startEdit(p)}
                    onDelete={() => removePost(wallPath(p.id))}
                  />
                )}
                {p.author_id !== data.me && <ReportAct id={p.id} ctx={ctx} />}
              </span>

              {reporting === p.id && (
                <ReportBox
                  busy={busy} t={t}
                  onCancel={() => setReporting(null)}
                  onSend={sendReport(reportPath(p.id))}
                />
              )}

              {p.comments.map((c) => (
                <CommentRow key={c.id} c={c} path={wallPath(c.id)} ctx={ctx} />
              ))}

              {replyTo === p.id && (
                <span className="es-compose">
                  <textarea
                    rows={2}
                    maxLength={MAX_BODY}
                    value={replyDraft}
                    placeholder={t('commentPlaceholder')}
                    onChange={(e) => setReplyDraft(e.target.value)}
                  />
                  <button
                    type="button"
                    className="es-btn"
                    disabled={busy || !replyDraft.trim()}
                    onClick={() => { const body = replyDraft.trim();
                      setReplyDraft(''); setReplyTo(null);
                      act(apiRequest('POST', `/event/${eventId}/wall`,
                                     { body, parent_id: p.id })); }}
                  >
                    {t('post')}
                  </button>
                </span>
              )}
            </span>
          </div>
        )))}
      </section>
    </div>
  );
};

export default EventSocial;
